import { motion } from "framer-motion";
import {
  Sofa,
  BedDouble,
  Layers,
  Home,
  LayoutGrid,
  ShieldCheck,
  MessageCircle,
  Sparkles,
  Info,
} from "lucide-react";
import { PRICE_CATEGORIES, waLink } from "../data/content";

const ICONS = {
  sofa: Sofa,
  bed: BedDouble,
  carpet: Layers,
  roof: Home,
  terrace: LayoutGrid,
  shield: ShieldCheck,
};

const PriceCard = ({ category, index }) => {
  const Icon = ICONS[category.icon];
  const message = `Olá! Gostaria de pedir um orçamento para ${category.title}.`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.1 }}
      data-testid={`price-card-${category.id}`}
      className="card-glow relative flex flex-col overflow-hidden rounded-3xl border border-sky-400/20 bg-gradient-to-b from-[#162550] to-[#101D42]"
    >
      {category.badge && (
        <span
          data-testid={`price-badge-${category.id}`}
          className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1128]"
        >
          <Sparkles className="h-3 w-3" />
          {category.badge}
        </span>
      )}

      <div className="flex items-center gap-3 border-b border-white/10 px-6 pt-6 pb-4">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-400/15 text-sky-300 ring-1 ring-sky-400/30">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-white leading-tight">
            {category.title}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{category.subtitle}</p>
        </div>
      </div>

      <ul className="flex-1 px-6 py-2">
        {category.items.map((item) => (
          <li
            key={item.name}
            className={`price-row flex items-center justify-between gap-4 border-b border-white/5 py-3.5 ${
              item.addon ? "text-sky-300" : ""
            }`}
          >
            <span
              className={`text-sm sm:text-[15px] ${
                item.addon ? "font-medium" : "text-slate-200"
              }`}
            >
              {item.addon ? `+ ${item.name}` : item.name}
            </span>
            <span className="font-display text-lg sm:text-xl font-extrabold text-sky-300 whitespace-nowrap">
              {item.price}
            </span>
          </li>
        ))}
      </ul>

      {category.note && (
        <p
          data-testid={`price-note-${category.id}`}
          className="mx-6 mb-2 flex items-start gap-2 text-xs text-slate-400 leading-relaxed"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-sky-400" />
          {category.note}
        </p>
      )}

      <div className="px-6 pb-6 pt-3">
        <a
          href={waLink(message)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`price-quote-button-${category.id}`}
          className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1eb857] px-5 py-3 text-sm font-bold text-[#06281a] transition-colors"
        >
          <MessageCircle className="h-4 w-4" />
          Pedir Orçamento
        </a>
      </div>
    </motion.article>
  );
};

export const PriceTables = () => {
  return (
    <section
      id="precos"
      data-testid="prices-section"
      className="py-20 sm:py-28 bg-[#0B1430] relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_0%,rgba(0,150,255,0.1),transparent_65%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span
            data-testid="prices-badge"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 px-5 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-[#0A1128]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Tabela de Preços
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Preços claros, sem surpresas
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Escolha o serviço e peça o seu orçamento gratuito diretamente no
            WhatsApp. Respondemos em minutos.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {PRICE_CATEGORIES.map((category, i) => (
            <PriceCard key={category.id} category={category} index={i} />
          ))}
        </div>

        <p
          data-testid="prices-disclaimer"
          className="mt-10 text-center text-xs sm:text-sm text-slate-500"
        >
          Valores de tabela. Para casos especiais ou grandes áreas, peça um
          orçamento personalizado — é gratuito e sem compromisso.
        </p>
      </div>
    </section>
  );
};
